import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import {
  profileImageKey,
  profileImageRequest,
  useProfileImage,
} from "../../lib/profile-image";
import "./profile-image.css";

export function ProfileImage({ size = 36 }: { size?: number }) {
  const query = useProfileImage();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = query.data?.url;
  return (
    <span className="user-profile-image" style={{ width: size, height: size }}>
      {url && url !== failedUrl ? (
        <img
          src={url}
          alt="プロフィール画像"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <UserRound size={Math.round(size / 1.7)} aria-hidden />
      )}
    </span>
  );
}

export function ProfileImageEditor() {
  const query = useProfileImage();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const validationId = useRef(0);
  useEffect(
    () => () => {
      validationId.current++;
    },
    [],
  );
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const mutation = useMutation({
    mutationFn: ({
      action,
      file: chosen,
      email,
    }: {
      action: "PUT" | "DELETE";
      file?: File;
      email: string;
    }) =>
      queryClient
        .cancelQueries({ queryKey: profileImageKey(email) })
        .then(() => profileImageRequest(action, chosen))
        .then((data) => ({
          data,
          email,
          action,
        })),
    onSuccess: ({ data, email, action }) => {
      queryClient.setQueryData(profileImageKey(email), data);
      setFile(null);
      setPreview(null);
      setConfirmDelete(false);
      setError("");
      setMessage(
        action === "DELETE"
          ? "プロフィール画像を削除しました。"
          : "プロフィール画像を保存しました。",
      );
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : "保存できませんでした。"),
  });
  const busy = validating || mutation.isPending;
  const select = async (selected?: File) => {
    if (!selected) return;
    setError("");
    setMessage("");
    setConfirmDelete(false);
    if (!["image/png", "image/jpeg", "image/webp"].includes(selected.type)) {
      setError("PNG・JPEG・WebPの画像を選択してください。");
      return;
    }
    if (selected.size > 2 * 1024 * 1024) {
      setError("画像は2MB以下にしてください。");
      return;
    }
    const id = ++validationId.current;
    const url = URL.createObjectURL(selected);
    setValidating(true);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      if (img.naturalWidth * img.naturalHeight > 16000000) throw new Error();
      if (id !== validationId.current) {
        URL.revokeObjectURL(url);
        return;
      }
      setFile(selected);
      setPreview(url);
    } catch {
      URL.revokeObjectURL(url);
      if (id === validationId.current)
        setError(
          "画像を読み込めません。1600万画素以下の画像を選択してください。",
        );
    } finally {
      if (id === validationId.current) setValidating(false);
    }
  };
  return (
    <section
      className="profile-image-editor"
      aria-labelledby="profile-image-heading"
    >
      <h2 id="profile-image-heading">プロフィール画像</h2>
      {query.isLoading && query.email && (
        <p role="status">画像を読み込み中...</p>
      )}
      {query.isError && (
        <div role="alert">
          <p>プロフィール画像を取得できませんでした。</p>
          <button onClick={() => void query.refetch()}>
            <RefreshCw size={16} />
            再読み込み
          </button>
        </div>
      )}
      <>
        <div className="profile-image-preview">
          {preview ? (
            <img src={preview} alt="新しいプロフィール画像のプレビュー" />
          ) : (
            <ProfileImage size={88} />
          )}
        </div>
        <label className="profile-image-upload">
          <Upload size={16} />
          画像を選択
          <input
            aria-label="プロフィール画像を選択"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={busy || !query.email}
            onChange={(e) => {
              void select(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <p className="profile-image-help">PNG・JPEG・WebP / 2MBまで</p>
        <div className="profile-image-actions">
          <button
            disabled={!file || busy || !query.email}
            onClick={() => {
              setError("");
              setMessage("");
              if (file && query.email)
                mutation.mutate({ action: "PUT", file, email: query.email });
            }}
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            保存
          </button>
          {file && (
            <button
              disabled={busy}
              onClick={() => {
                setFile(null);
                setPreview(null);
                setError("");
              }}
            >
              キャンセル
            </button>
          )}
          {!!query.data?.url && !file && (
            <button
              disabled={busy}
              onClick={() => {
                setConfirmDelete(true);
                setMessage("");
              }}
            >
              <Trash2 size={16} />
              画像を削除
            </button>
          )}
        </div>
        {confirmDelete && (
          <div
            className="profile-image-delete"
            role="group"
            aria-label="画像削除の確認"
          >
            <p>プロフィール画像を削除しますか？</p>
            <div className="profile-image-actions">
              <button
                disabled={busy || !query.email}
                onClick={() => {
                  if (query.email)
                    mutation.mutate({ action: "DELETE", email: query.email });
                }}
              >
                削除する
              </button>
              <button disabled={busy} onClick={() => setConfirmDelete(false)}>
                キャンセル
              </button>
            </div>
          </div>
        )}
      </>
      {error && (
        <p role="alert" className="profile-image-error">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
